import { TPartnerResDto } from "@/api/services/main/partner/dtos/form-partner-req.dto";
import { TGetPartnerResDto } from "@/api/services/main/partner/dtos/get-partner.dto";
import { TGetPartnerListReqDto, TGetPartnerListResDto } from "@/api/services/main/partner/dtos/get-partner-list.dto";
import {
  TGetPartnerFieldOptionsReqDto,
  TGetPartnerFieldOptionsResDto,
  TGetPartnerFieldOptionsBatchResDto,
  TPartnerFieldOptionsField,
  PARTNER_FIELD_OPTIONS_FIELDS,
} from "@/api/services/main/partner/dtos/get-partner-field-options.dto";
import { TPartnerReqDto, TUpdatePartnerResDto } from "@/api/services/main/partner/dtos/form-partner-req.dto";
import { CACHED_TIME } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { truncateWithEllipsis } from "@/utils/string";
import { FILTER_LABEL_MAX_LENGTH } from "@/features/partner/components/partner-list/partner-filter-shared";
import { useQuery, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { TOption } from "@/types/common";
import { AsyncSearchConfig, AsyncSearchPage, buildAsyncSearchQueryKey } from "@/hooks/useSearchList";
import { TPartner } from "@/api/entities/partner.entity";
import { TPartnerImportRow, TPartnerRowExcel } from "@/features/partner/types/partner-import";

/**
 * Single fetch for GET /partners (mirror getTalentListApi). No params → BE default (page 1, perPage 50).
 * Pass { page, perPage, search, partnerId } for paginated/lazy/search calls.
 */
export const fetchPartnerListApi = async (params?: TGetPartnerListReqDto): Promise<TGetPartnerListResDto> => {
  const rs = await clientApi.get<TGetPartnerListResDto>("/partners", { params });
  return rs.data;
};

/** Paginated table hook for the Partner list page (mirrors useTalentList). */
export const usePartnerList = (params: TGetPartnerListReqDto, enabled?: boolean) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PARTNER.FETCH_LIST, "table", params],
    queryFn: () => fetchPartnerListApi(params),
    enabled: enabled !== false,
  });
};

const PARTNER_PER_PAGE = 50;

type TPartnerPageItem = { id: number; partnerName?: string };
type TPartnerPage = { items: TPartnerPageItem[]; nextPage?: number; total?: number };

/**
 * Lazy-load (infinite-scroll) hook for the パートナー名 dropdown. First page omits page/perPage
 * (BE defaults to page 1, perPage 50); `hasNextPage` comes from `pagination.nextPage`.
 */
export const useGetPartnerList = (search: string, partnerId?: number | null) => {
  const query = useInfiniteQuery({
    queryKey: [QUERY_KEYS.PARTNER.FETCH_LIST, "infinite", search, partnerId ?? null],
    queryFn: async ({ pageParam }): Promise<TPartnerPage> => {
      const page = pageParam as number;
      // First page: omit page/perPage (BE default). Next pages send them. Search/partnerId only when present.
      const rs = await fetchPartnerListApi({
        ...(page > 1 ? { page, perPage: PARTNER_PER_PAGE } : {}),
        ...(search ? { search } : {}),
        ...(partnerId ? { partnerId } : {}),
      });
      return { items: rs.data, nextPage: rs.pagination?.nextPage ?? undefined, total: rs.pagination?.total };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage as TPartnerPage).nextPage,
    staleTime: CACHED_TIME,
    // Drop cache on close → consumers (master-data, forms) refetch fresh page 1 on next mount.
    gcTime: 0,
  });

  const options = useMemo<TOption[]>(() => {
    const seen = new Set<number>();
    const result: TOption[] = [];
    (query.data?.pages ?? []).forEach((page) =>
      (page as TPartnerPage).items.forEach((item) => {
        if (seen.has(item.id)) return; // dedup: appended selected record may also appear on its natural page
        seen.add(item.id);
        result.push({ label: truncateWithEllipsis(item.partnerName ?? "", 50), value: String(item.id) });
      }),
    );
    return result;
  }, [query.data]);

  return {
    options,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetching: query.isFetching,
    // True only while the NEXT page is loading — used to gate scroll-triggered fetches
    isFetchingNextPage: query.isFetchingNextPage,
  };
};

/** Config for the パートナー名 dropdown — pass to <ComboboxMultiAsync searchConfig={partnerSearchConfig} />. */
export const partnerSearchConfig: AsyncSearchConfig<TPartner> = {
  queryKey: [QUERY_KEYS.PARTNER.FETCH_LIST],
  fetchPage: async (params) => {
    const rs = await fetchPartnerListApi(params);
    return { items: rs.data, total: rs.pagination?.total, nextPage: rs.pagination?.nextPage ?? undefined };
  },
  toOption: (item) => ({ label: truncateWithEllipsis(item.partnerName ?? "", 50), value: String(item.id) }),
};

export const fetchPartnerFieldOptionsApi = async (params: TGetPartnerFieldOptionsReqDto): Promise<TGetPartnerFieldOptionsResDto> => {
  const rs = await clientApi.get<TGetPartnerFieldOptionsResDto>("/partners/field-options", { params });
  return rs.data;
};

const toFieldOptionsPage = (rs: TGetPartnerFieldOptionsResDto): AsyncSearchPage<string> => ({
  items: rs.data,
  total: rs.pagination?.total,
  nextPage: rs.pagination?.nextPage ?? undefined,
});

const fieldOptionsQueryKey = (field: TPartnerFieldOptionsField) => [QUERY_KEYS.PARTNER.FETCH_LIST, "field-options", field];

const createPartnerFieldOptionsSearchConfig = (field: TPartnerFieldOptionsField): AsyncSearchConfig<string> => ({
  queryKey: fieldOptionsQueryKey(field),
  fetchPage: async (params) => toFieldOptionsPage(await fetchPartnerFieldOptionsApi({ field, ...params })),
  toOption: (value) => ({ label: truncateWithEllipsis(value, FILTER_LABEL_MAX_LENGTH), value }),
});

// Configs for the Partner list filter panel's パートナー管理番号/パートナー名/所在地/担当者 async
// dropdowns (GET /partners/field-options). Pass to <ComboboxMultiAsync searchConfig={...} />.
export const partnerManagementNumberSearchConfig = createPartnerFieldOptionsSearchConfig("managementNumber");
export const partnerNameFieldSearchConfig = createPartnerFieldOptionsSearchConfig("partnerName");
export const partnerLocationSearchConfig = createPartnerFieldOptionsSearchConfig("location");
export const partnerContactPersonSearchConfig = createPartnerFieldOptionsSearchConfig("contactPerson");

export const fetchPartnerFieldOptionsBatchApi = async (): Promise<TGetPartnerFieldOptionsBatchResDto> => {
  const rs = await clientApi.get<TGetPartnerFieldOptionsBatchResDto>("/partners/field-options/batch");
  return rs.data;
};

/**
 * Fetches page 1 (unfiltered) of all 4 field-options dropdowns in one request, so the filter panel doesn't
 * fire 4 parallel requests on mount. Once the batch resolves, it writes each field's page-1 straight into
 * that dropdown's own query cache (`queryClient.setQueryData`, keyed via `buildAsyncSearchQueryKey` so it
 * matches exactly what useSearchList reads) — `initialData` can't be used here since useSearchList's query
 * already exists (mounted disabled) by the time batch data arrives, and React Query only applies `initialData`
 * the first time a query is created. Returns one `enabled` flag per field: false until its cache is seeded,
 * so <ComboboxMultiAsync enabled={...} /> only starts fetching on its own once there's nothing left to seed.
 */
export const usePartnerFieldOptionsBatch = () => {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: [QUERY_KEYS.PARTNER.FETCH_LIST, "field-options", "batch"],
    queryFn: fetchPartnerFieldOptionsBatchApi,
    gcTime: 0,
  });
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (!data) return;
    PARTNER_FIELD_OPTIONS_FIELDS.forEach((field) => {
      const queryKey = buildAsyncSearchQueryKey(fieldOptionsQueryKey(field));
      queryClient.setQueryData(queryKey, { pages: [toFieldOptionsPage(data[field])], pageParams: [1] });
    });
    setSeeded(true);
  }, [data, queryClient]);

  return useMemo(() => {
    return Object.fromEntries(PARTNER_FIELD_OPTIONS_FIELDS.map((field) => [field, seeded])) as Record<TPartnerFieldOptionsField, boolean>;
  }, [seeded]);
};

export const createPartnerApi = async (payload: TPartnerReqDto): Promise<TPartnerResDto> => {
  const rs = await clientApi.post<TPartnerResDto>("/partners", payload);
  return rs.data;
};

export const updatePartnerApi = async (id: string | number, payload: TPartnerReqDto): Promise<TUpdatePartnerResDto> => {
  const rs = await clientApi.put<TUpdatePartnerResDto>(`/partners/${id}`, payload);
  return rs.data;
};

export const fetchPartnerInfoApi = async (id: string | number): Promise<TGetPartnerResDto> => {
  const { data } = await clientApi.get<TGetPartnerResDto>(`/partners/${id}`);
  return data;
};

export const usePartnerInfo = (id?: number) => {
  const { partnerId } = useParams();

  const fetchId = id ?? partnerId;
  return useQuery({
    queryKey: [QUERY_KEYS.PARTNER.FETCH_DETAIL, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchPartnerInfoApi(fetchId) : undefined;
    },
    enabled: Boolean(fetchId),
  });
};

export const validateImportPartnerApi = async (payload: TPartnerRowExcel[]) => {
  const rs = await clientApi.post("/partners/validate-import", payload);
  return rs.data;
};

export const importPartnersApi = async (payload: TPartnerImportRow[]) => {
  const rs = await clientApi.post("/partners/import", payload);
  return rs.data;
};
